import { useCallback, useContext, useState } from "react"

import { Link, useLocalSearchParams } from "expo-router"

import { Asset, Link as HALLink, Index, Instrument, InstrumentCollection } from "wealthmode-frontend/backend"
import {
  Box,
  Button,
  ButtonText,
  Checkbox,
  CheckboxIcon,
  CheckboxIndicator,
  CheckboxLabel,
  HStack,
  Text,
  VStack,
} from "wealthmode-frontend/gluestack/ui"
import { CheckIcon } from "wealthmode-frontend/icons"

import { DataTable, IDLink } from "@/components"
import { AssetLinkModal } from "@/components/widgets"
import { BackendContext } from "@/context"

const BLOCKED_WHERE = "eq(asset_blocked,true)"
const UNLINKED_WHERE = "eq(asset_id,null)"

export default function Instruments() {
  const backend = useContext(BackendContext)
  const { offset, limit } = useLocalSearchParams<{ offset?: string; limit?: string }>()
  const [blockedOnly, setBlockedOnly] = useState(false)
  const [unlinkedOnly, setUnlinkedOnly] = useState(false)
  const [refreshKey, setRefreshKey] = useState<number | undefined>(undefined)

  const load = useCallback(async () => {
    var data = backend.link("data").resolve() as Index
    await data.fetch({ reload: true })

    const link = data.link("instruments")
    if (link == null || link.type == null) {
      return undefined
    }

    // A Link memoizes what it resolves to, so the filter needs a collection of
    // its own. Everything goes in the URL rather than in fetch options, which
    // replace its query wholesale, so reloads and pagination keep the filter.
    const href = new URL(link.href.toString())
    const where = [blockedOnly ? BLOCKED_WHERE : null, unlinkedOnly ? UNLINKED_WHERE : null].filter(
      (clause) => clause != null,
    )
    if (where.length === 1) {
      href.searchParams.set("where", where[0])
    } else if (where.length > 1) {
      href.searchParams.set("where", `and(${where.join(",")})`)
    }
    if (offset) {
      href.searchParams.set("offset", offset)
    }
    if (limit) {
      href.searchParams.set("limit", limit)
    }

    const instruments = new HALLink(href, link.type).resolve() as InstrumentCollection
    await instruments.fetch({ reload: true })

    return instruments
  }, [backend, offset, limit, blockedOnly, unlinkedOnly])

  return (
    <Box className="flex-1">
      <VStack className="flex-1 gap-4">
        <HStack className="flex-1">
          <Box className="flex-1">
            <DataTable
              title={"Instruments"}
              load={load}
              refreshKey={refreshKey}
              filters={{
                active: [blockedOnly ? "Blocked only" : null, unlinkedOnly ? "Unlinked only" : null].filter(
                  (label) => label != null,
                ),
                controls: (
                  <VStack className="gap-3">
                    <Checkbox
                      value="blocked"
                      isChecked={blockedOnly}
                      onChange={setBlockedOnly}
                      size="md"
                    >
                      <CheckboxIndicator>
                        <CheckboxIcon as={CheckIcon} />
                      </CheckboxIndicator>
                      <CheckboxLabel>Blocked only</CheckboxLabel>
                    </Checkbox>
                    <Checkbox
                      value="unlinked"
                      isChecked={unlinkedOnly}
                      onChange={setUnlinkedOnly}
                      size="md"
                    >
                      <CheckboxIndicator>
                        <CheckboxIcon as={CheckIcon} />
                      </CheckboxIndicator>
                      <CheckboxLabel>Unlinked only</CheckboxLabel>
                    </Checkbox>
                  </VStack>
                ),
              }}
              header={[
                { id: "id", title: "ID", shrink: true },
                { id: "symbol", title: "Symbol", shrink: true },
                { id: "asset", title: "Asset", noTruncate: true },
                { id: "type", title: "Type", shrink: true },
                { id: "name", title: "Name" },
              ]}
              render={(model: Instrument, field: string) => {
                if (field === "id") {
                  return (
                    <IDLink
                      href={`/instruments/${model.attributes.id}`}
                      id={model.attributes.id}
                    />
                  )
                }

                if (field === "asset") {
                  const asset = model.embed("asset") as Asset | null
                  if (asset?.id) {
                    return (
                      <Link href={`/assets/${asset.id}`}>
                        <Text className="text-markdown-link hover:opacity-80 underline">{asset.attributes.symbol}</Text>
                      </Link>
                    )
                  }

                  return (
                    <HStack className="items-start gap-2">
                      <AssetLinkButton
                        instrument={model}
                        onLinked={() => setRefreshKey((key) => (key ?? 0) + 1)}
                      />
                      <AssetLinkingButton instrument={model} />
                    </HStack>
                  )
                }

                return model.get(field)
              }}
            />
          </Box>
        </HStack>
      </VStack>
    </Box>
  )
}

function AssetLinkButton({ instrument, onLinked }: { instrument: Instrument; onLinked: () => void }) {
  const [open, setOpen] = useState(false)

  async function link(assetID: string) {
    await curate(instrument, { asset_id: assetID })
    onLinked()
  }

  return (
    <>
      <Button
        size="xs"
        action="secondary"
        onPress={() => setOpen(true)}
      >
        <ButtonText>Link</ButtonText>
      </Button>
      <AssetLinkModal
        isOpen={open}
        onClose={() => setOpen(false)}
        onLink={link}
      />
    </>
  )
}

function AssetLinkingButton({ instrument }: { instrument: Instrument }) {
  const [blocked, setBlocked] = useState(instrument.attributes.asset_blocked)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function toggle() {
    setSaving(true)
    setError(null)

    try {
      await curate(instrument, { asset_blocked: !blocked })

      await instrument.fetch({ reload: true })
      setBlocked(instrument.attributes.asset_blocked)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  if (blocked == null) {
    return null
  }

  return (
    <VStack className="items-start gap-1">
      <Button
        size="xs"
        action="secondary"
        disabled={saving}
        onPress={toggle}
      >
        <ButtonText>{saving ? "Saving..." : blocked ? "Unblock Linking" : "Block Linking"}</ButtonText>
      </Button>
      {error ? <Text className="text-xs text-foreground-secondary whitespace-pre-wrap">{error}</Text> : null}
    </VStack>
  )
}

/** curate changes the curation fields it is handed and leaves the rest. */
async function curate(instrument: Instrument, curation: { asset_blocked?: boolean; asset_id?: string }) {
  const self = instrument.link("self")
  if (!self) {
    throw new Error("Instrument resource is not available")
  }

  const response = await self.send({
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(curation),
  })
  if (!response.ok) {
    const text = await response.text()
    throw new Error(text || response.statusText)
  }
}
